import { useRef, useState, useMemo } from "react";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { usuarios, formatHora } from "../../data/mock";
import {
  Repeat,
  CheckCircle2,
  Camera,
  Lock,
  X,
  Sun,
  Moon,
  UserMinus,
  UserCheck,
  FileText,
  AlertTriangle,
  Image,
} from "lucide-react";
import { toast } from "sonner";

const TURNOS = [
  { value: "diurno", label: "Diurno (07h às 19h)", icon: Sun },
  { value: "noturno", label: "Noturno (19h às 07h)", icon: Moon },
];

const isOperacional = (v) => /^VG-\d+$/.test(v.matricula);

export default function PassagemTurnoMobile() {
  const { vigilantes, registrarPassagem, passagens } = useApp();

  const [step, setStep] = useState("form");
  const [turno, setTurno] = useState("");
  const [vigSaindoId, setVigSaindoId] = useState("");
  const [vigRecebendoId, setVigRecebendoId] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [foto, setFoto] = useState(null);
  const [pendencias, setPendencias] = useState("");
  const [senhaInput, setSenhaInput] = useState("");
  const [senhaErro, setSenhaErro] = useState(false);
  const [passagemRegistrada, setPassagemRegistrada] = useState(null);

  const fotoInputRef = useRef(null);

  const operacionais = useMemo(
    () => vigilantes.filter(isOperacional),
    [vigilantes],
  );

  const optsRecebendo = useMemo(
    () => operacionais.filter((v) => v.id !== vigSaindoId),
    [operacionais, vigSaindoId],
  );

  const vigSaindoNome = operacionais.find((v) => v.id === vigSaindoId)?.nome ?? "";
  const vigRecebendoNome = operacionais.find((v) => v.id === vigRecebendoId)?.nome ?? "";

  const podeConfirmar = !!(turno && vigSaindoId && vigRecebendoId);

  const handleFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFoto(URL.createObjectURL(file));
  };

  const abrirSenha = () => {
    if (!podeConfirmar) {
      toast.error("Preencha turno, vigilante saindo e vigilante recebendo");
      return;
    }
    setSenhaInput("");
    setSenhaErro(false);
    setStep("senha");
  };

  const confirmarSenha = () => {
    const usuario = usuarios.find((u) => u.vigilanteId === vigRecebendoId);
    if (!usuario) {
      toast.error("Usuário não encontrado para este vigilante");
      return;
    }

    const storageKey = `senha_usuario_apresentacao-liberty-v2_${usuario.id}`;
    const senhaAtual = localStorage.getItem(storageKey) ?? usuario.senha;

    if (senhaInput !== senhaAtual) {
      setSenhaErro(true);
      return;
    }

    const nova = registrarPassagem({
      paraVigilante: vigRecebendoNome,
      resumo: observacoes.trim() || "Passagem de turno realizada sem observações.",
      turno,
      vigSaindoId,
      vigSaindoNome,
      vigRecebendoId,
      vigRecebendoNome,
      observacoes,
      pendencias,
      fotoAnexada: !!foto,
    });

    setPassagemRegistrada(nova);
    setStep("sucesso");
  };

  const resetar = () => {
    setStep("form");
    setTurno("");
    setVigSaindoId("");
    setVigRecebendoId("");
    setObservacoes("");
    setFoto(null);
    setPendencias("");
    setSenhaInput("");
    setSenhaErro(false);
    setPassagemRegistrada(null);
  };

  return (
    <MobileLayout title="Passagem de Turno" scrollable>
      {step === "sucesso" ? (
        <SucessoScreen
          passagem={passagemRegistrada}
          onNova={resetar}
        />
      ) : (
        <>
          {/* Form */}
          <div className="space-y-4 mb-5">
            {/* Turno */}
            <section className="rounded-2xl bg-[#0a1128] border border-white/10 p-5">
              <p className="tiny-label text-slate-400 mb-3">Turno</p>
              <div className="grid grid-cols-2 gap-3">
                {TURNOS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTurno(value)}
                    className={`flex flex-col items-center gap-2 py-4 rounded-xl border text-sm font-semibold transition-colors ${
                      turno === value
                        ? value === "diurno"
                          ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                          : "bg-blue-500/20 border-blue-500/50 text-blue-200"
                        : "bg-white/5 border-white/10 text-slate-400"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </button>
                ))}
              </div>
            </section>

            {/* Vigilantes */}
            <section className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 space-y-4">
              <div>
                <label className="tiny-label text-slate-400 mb-1.5 flex items-center gap-1.5">
                  <UserMinus className="w-3.5 h-3.5" />
                  Vigilante saindo
                </label>
                <select
                  value={vigSaindoId}
                  onChange={(e) => {
                    setVigSaindoId(e.target.value);
                    if (vigRecebendoId === e.target.value) setVigRecebendoId("");
                  }}
                  className="w-full px-3.5 py-3 rounded-lg bg-[#040914] border border-white/10 text-sm focus:outline-none focus:border-blue-500/50"
                >
                  <option value="">Selecione o vigilante</option>
                  {operacionais.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nome} — {v.matricula}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="tiny-label text-slate-400 mb-1.5 flex items-center gap-1.5">
                  <UserCheck className="w-3.5 h-3.5" />
                  Vigilante recebendo
                </label>
                <select
                  value={vigRecebendoId}
                  onChange={(e) => setVigRecebendoId(e.target.value)}
                  className="w-full px-3.5 py-3 rounded-lg bg-[#040914] border border-white/10 text-sm focus:outline-none focus:border-blue-500/50"
                  disabled={!vigSaindoId}
                >
                  <option value="">Selecione o vigilante</option>
                  {optsRecebendo.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nome} — {v.matricula}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            {/* Observações */}
            <section className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 space-y-3">
              <label className="tiny-label text-slate-400 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Observações do turno
              </label>

              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={4}
                placeholder="Descreva ocorrências, pendências e situação geral do posto..."
                className="w-full px-3.5 py-3 rounded-lg bg-[#040914] border border-white/10 text-sm focus:outline-none focus:border-blue-500/50 resize-none"
              />

              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFoto}
              />

              {foto ? (
                <div className="relative rounded-xl overflow-hidden border border-white/10">
                  <img
                    src={foto}
                    alt="Foto do turno"
                    className="w-full max-h-48 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFoto(null)}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fotoInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                  Adicionar foto
                </button>
              )}
            </section>

            {/* Pendências */}
            <section className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 space-y-3">
              <label className="tiny-label text-slate-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Pendências para o próximo turno
                <span className="text-slate-600">(opcional)</span>
              </label>

              <textarea
                value={pendencias}
                onChange={(e) => setPendencias(e.target.value)}
                rows={3}
                placeholder="Ex.: portão lateral com travamento lento, câmera 3 offline..."
                className="w-full px-3.5 py-3 rounded-lg bg-[#040914] border border-white/10 text-sm focus:outline-none focus:border-blue-500/50 resize-none"
              />
            </section>

            {/* Confirm button */}
            <button
              type="button"
              onClick={abrirSenha}
              disabled={!podeConfirmar}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-sm transition-colors ${
                podeConfirmar
                  ? "bg-emerald-500 text-black"
                  : "bg-white/5 border border-white/10 text-slate-500 cursor-not-allowed"
              }`}
            >
              <Lock className="w-4 h-4" />
              {vigRecebendoNome
                ? `Assinar e assumir turno — ${vigRecebendoNome}`
                : "Assinar e assumir turno"}
            </button>
          </div>

          {/* Histórico */}
          {passagens.length > 0 && (
            <>
              <h3 className="tiny-label text-slate-400 mb-3">Últimas passagens</h3>
              <div className="space-y-2">
                {passagens.slice(0, 4).map((p) => (
                  <div
                    key={p.id}
                    className="rounded-xl bg-[#0a1128] border border-white/5 p-4"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Repeat className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-xs font-medium">
                        Para {p.paraVigilante}
                      </span>
                      <span className="text-xs text-slate-500 ml-auto font-mono-data">
                        {formatHora(p.criadoEm)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">
                      {p.resumo}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Password modal */}
      {step === "senha" && (
        <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-end justify-center">
          <div className="w-full max-w-lg rounded-t-3xl bg-[#0a1128] border-t border-white/10 p-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="tiny-label text-emerald-300">Confirmação de identidade</p>
                <h3 className="font-display font-bold text-lg mt-1">
                  Senha de {vigRecebendoNome}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setStep("form")}
                className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <input
                type="password"
                value={senhaInput}
                onChange={(e) => {
                  setSenhaInput(e.target.value);
                  setSenhaErro(false);
                }}
                onKeyDown={(e) => e.key === "Enter" && confirmarSenha()}
                placeholder="Digite a senha"
                autoFocus
                className={`w-full px-4 py-3.5 rounded-xl bg-[#040914] border text-sm focus:outline-none ${
                  senhaErro
                    ? "border-red-500/60 focus:border-red-500"
                    : "border-white/10 focus:border-blue-500/50"
                }`}
              />
              {senhaErro && (
                <p className="text-xs text-red-400 mt-1.5">
                  Senha incorreta. Tente novamente.
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={confirmarSenha}
              disabled={!senhaInput}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold transition-colors ${
                senhaInput
                  ? "bg-emerald-500 text-black"
                  : "bg-white/5 border border-white/10 text-slate-500 cursor-not-allowed"
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              Confirmar e registrar passagem
            </button>
          </div>
        </div>
      )}
    </MobileLayout>
  );
}

function SucessoScreen({ passagem, onNova }) {
  const turnoLabel = {
    diurno: "Diurno (07h às 19h)",
    noturno: "Noturno (19h às 07h)",
  };

  return (
    <div className="flex flex-col items-center text-center py-6 px-2">
      <div className="w-20 h-20 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mb-5">
        <CheckCircle2 className="w-10 h-10 text-emerald-400" />
      </div>

      <h2 className="font-display font-black text-2xl mb-1">
        Passagem registrada
      </h2>
      <p className="text-slate-400 text-sm mb-6">
        Turno assumido com sucesso
      </p>

      <div className="w-full rounded-2xl bg-[#0a1128] border border-white/10 p-5 text-left space-y-3 mb-6">
        {passagem?.turno && (
          <Row label="Turno" value={turnoLabel[passagem.turno] ?? passagem.turno} />
        )}
        {passagem?.vigSaindoNome && (
          <Row label="Saindo" value={passagem.vigSaindoNome} />
        )}
        {passagem?.vigRecebendoNome && (
          <Row label="Assumindo" value={passagem.vigRecebendoNome} />
        )}
        <Row
          label="Registrado em"
          value={passagem?.criadoEm ? formatHora(passagem.criadoEm) : "—"}
        />
        {passagem?.fotoAnexada && (
          <div className="flex items-center gap-2 text-xs text-slate-400 pt-1 border-t border-white/5">
            <Image className="w-3.5 h-3.5" />
            Foto anexada ao registro
          </div>
        )}
        {passagem?.pendencias?.trim() && (
          <div className="pt-2 border-t border-white/5">
            <p className="tiny-label text-amber-300 mb-1">Pendências registradas</p>
            <p className="text-xs text-slate-300 leading-relaxed">
              {passagem.pendencias}
            </p>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onNova}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-white/5 border border-white/10 text-slate-200 font-semibold"
      >
        <Repeat className="w-4 h-4" />
        Nova passagem
      </button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-xs font-medium text-slate-200 text-right">{value}</span>
    </div>
  );
}
